import { InputAdornment, TextField, IconButton, Tooltip } from "@mui/material";
import { RiSearchLine, RiCloseLine } from "@remixicon/react";
import type { title } from "process";
import { useState, useEffect, useRef, type FunctionComponent } from "react";

/** Tipos de transformación de texto */
export type TextTransform = "title" | "uppercase" | "none";

interface SearchInputMUIProps {
    /** Valor del input */
    value?: string;
    /** Callback que se ejecuta cuando cambia el valor del input */
    onChange?: (value: string) => void;
    /** Callback que se ejecuta después del debounce, al presionar Enter o al hacer clic en el ícono de búsqueda */
    onSearch?: (value: string) => void;
    /** Callback que se ejecuta al limpiar el input */
    onClear?: () => void;
    /** Placeholder del input */
    placeholder?: string;
    /** Ancho del componente - puede ser string CSS o porcentaje */
    width?: string;
    /** Color del ícono de búsqueda */
    iconColor?: string;
    /** Mostrar botón de limpiar cuando hay texto */
    showClearButton?: boolean;
    /** Tiempo de debounce en milisegundos. Si es 0 o undefined, el debounce está deshabilitado */
    debounceTime?: number;
    /** Variante del TextField */
    variant?: "filled" | "outlined" | "standard";
    /** Transformación del texto: 'title' | 'uppercase' | 'none' */
    textTransform?: TextTransform;
    /** Clases adicionales */
    className?: string;
    /** Props adicionales para el TextField */
    textFieldProps?: any;
    /** Título para el tooltip del ícono de búsqueda */
    title?: string;
}

const SearchInputMUI: FunctionComponent<SearchInputMUIProps> = ({
    value: controlledValue,
    onChange,
    onSearch,
    onClear,
    placeholder = "Buscar...",
    width = "60%",
    iconColor = "var(--color-blue-dark)",
    showClearButton = true,
    debounceTime = 0,
    variant = "standard",
    textTransform = "uppercase",
    className,
    textFieldProps,
    title = "Buscar"
}) => {
    const [internalValue, setInternalValue] = useState(controlledValue || "");
    const inputRef = useRef<HTMLInputElement>(null);

    /**
     * Aplica la transformación de texto según el tipo especificado
     * @param text - Texto a transformar
     * @param transform - Tipo de transformación
     * @returns Texto transformado
     */
    const applyTextTransform = (text: string, transform: TextTransform): string => {
        switch (transform) {
            case "title":
                return text
                    .toLowerCase()
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            case "uppercase":
                return text.toUpperCase();
            case "none":
            default:
                return text;
        }
    };

    /**
     * Ejecuta la búsqueda de forma inmediata
     */
    const executeSearch = () => {
        if (onSearch) {
            onSearch(internalValue);
        }
    };

    /* Efecto para sincronizar el valor interno con el valor controlado */
    useEffect(() => {
        if (controlledValue !== undefined) {
            const transformedValue = applyTextTransform(controlledValue, textTransform);
            setInternalValue(transformedValue);
        }
    }, [controlledValue, textTransform]);

    /* Efecto para el debounce - solo se activa si debounceTime > 0 */
    useEffect(() => {
        if (!onSearch || !debounceTime || debounceTime <= 0) return;

        const timeoutId = setTimeout(() => {
            onSearch(internalValue);
        }, debounceTime);

        return () => clearTimeout(timeoutId);
    }, [internalValue, onSearch, debounceTime]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;
        const transformedValue = applyTextTransform(rawValue, textTransform);
        
        setInternalValue(transformedValue);
        
        /* Si hay un onChange, lo ejecutamos inmediatamente */
        if (onChange) {
            onChange(transformedValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        /* Si se presiona Enter, ejecutar la búsqueda inmediatamente */
        if (event.key === "Enter") {
            event.preventDefault();
            executeSearch();
        }
    };

    const handleSearchClick = () => {
        /* Ejecutar la búsqueda al hacer clic en el ícono */
        executeSearch();
    };

    const handleClear = () => {
        setInternalValue("");
        
        /* Ejecutar callbacks con valor vacío */
        if (onChange) {
            onChange("");
        }
        if (onClear) {
            onClear();
        }
        /* También ejecutar onSearch para limpiar resultados inmediatamente */
        if (onSearch) {
            onSearch("");
        }
        
        /* Mantener el foco en el input después de limpiar */
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        });
    };

    return (
        <TextField
            value={internalValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            variant={variant}
            className={className}
            inputProps={{
                ref: inputRef,
                ...textFieldProps?.inputProps
            }}
            sx={{
                width: width,
                ...textFieldProps?.sx
            }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <IconButton
                                onClick={handleSearchClick}
                                edge="start"
                                size="small"
                                sx={{ 
                                    color: iconColor,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                <Tooltip title={title} arrow>
                                    <RiSearchLine size={20} />
                                </Tooltip>
                            </IconButton>
                        </InputAdornment>
                    ),
                    endAdornment: showClearButton && internalValue ? (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClear}
                                edge="end"
                                size="small"
                                sx={{ 
                                    color: iconColor,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                <RiCloseLine size={20} />
                            </IconButton>
                        </InputAdornment>
                    ) : undefined,
                },
                ...textFieldProps?.slotProps
            }}
            {...textFieldProps}
        />
    );
};

export default SearchInputMUI;